/**
 * @file Shared permissions definition for Better-Auth admin plugin.
 *
 * Defines resources, actions, and roles (user, admin, superadmin) to be used
 * by both the API (server) and App (client) for consistent access control.
 */

// Define role constants for strong typing
export const Role = {
  Admin: "admin",
  SuperAdmin: "superadmin",
  User: "user",
} as const;

export type RoleType = (typeof Role)[keyof typeof Role];

import { createAccessControl } from "better-auth/plugins/access";
import { adminAc, defaultStatements } from "better-auth/plugins/admin/access";

// Define the resources and their custom actions
// We merge with defaultStatements to keep standard admin capabilities
export const statement = {
  ...defaultStatements,
  project: ["create", "share", "update", "delete"],
  system: ["manage"], // New 'system' resource for superadmin
  organization: ["create", "read", "update", "delete", "invite"],
} as const;

export const ac = createAccessControl(statement);

// User role: Basic access
// In a real app, 'user' might have specific permissions, usually scoped to their data.
// Here we define what a generic 'user' role *could* do if assigned globally,
// though usually users have permissions via 'member' role in organizations.
// For the purpose of the 'admin' plugin roles, these are global roles.
export const user = ac.newRole({
  organization: ["read"],
});

// Admin role: Standard admin plugin permissions + extra organization rights
export const admin = ac.newRole({
  ...adminAc.statements,
  organization: ["create", "update", "read", "invite"],
  project: ["create", "update"],
});

// Superadmin role: All admin permissions + system management + destructive actions
export const superadmin = ac.newRole({
  ...adminAc.statements,
  organization: ["create", "read", "update", "delete", "invite"],
  project: ["create", "share", "update", "delete"],
  system: ["manage"],
  user: [
    "create",
    "list",
    "get",
    "update",
    "delete",
    "ban",
    "impersonate",
    "set-role",
  ], // Full user control from adminAc statements + more if extended
});
